import { Col, DatePicker, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import queryKeys from "common/queryKey";
import { CALL_FEATCH_API } from "common/API";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { setFilter } from "redux/actions";
import {
  getClientid,
  getFilters,
  getManagingOffice,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import { utils, writeFileXLSX } from "xlsx";
import dayjs from "dayjs";

const RoundedSelect = styled(Select)`
  &:where(.ant-select) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select-focused) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select) .ant-select-selector:hover {
    border-radius: 15px !important;
  }
`;

function QualityCommitment() {
  const userList = useSelector((state) => getUserList(state));
  const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"));
  const clientId = useSelector(getClientid);
  const managingOfficeList = useSelector(getManagingOffice);
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);

  const isSuperAdmin = userInfo?.roleId === "2";

  const activeUsers = useMemo(() => {
    return userList?.filter(user => user?.statusid !== "0");
  }, [userList]);

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      className: "normal-column ",
      render: (val) => {
        return <span className="text-primary">{val}</span>;
      },
      onCell: (record) => ({
        onClick: () => {
          if (record?.IndependenceForm) {
            history.push(`/quality-commitment/${record.userid}`);
          }
        },
        style: { cursor: "pointer" },
      }),
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      className: "normal-column",
      render: (val) => {
        return <span>{val ? val : "-"}</span>;
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      className: "normal-column",
      ellipsis: {
        showTitle: true,
      },
      render: (val) => {
        return <span>{val === "null" || val === "" ? "-" : val}</span>;
      }
    },
    {
      title: "Email",
      dataIndex: "userid",
      key: "person_email_address",
      className: "normal-column",
      render: (val) => {
        return <span>{val === "null" || val === "" ? "-" : val}</span>;
      },
    },
    {
      title: "Submission Status",
      dataIndex: "status",
      key: "status",
      render: val => {
        if (val === "signed") return "Signed";
        if (val === "not_signed") return "Not Signed";
        return "-";
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "time",
      key: "declarationSubmittedDate",
      className: "normal-column",
      render: (val) => {
        return <span>{val && val !== undefined && val !== null && val !== "null" ? moment(val).format("DD-MM-YYYY") : "-"}</span>;
      }
    },
  ];

  if (finduser?.managingoffice[0] === "Head Office") {
    columns.push({
      title: "Managing Office",
      dataIndex: "managingoffice",
      key: "managingoffice",
      className: "normal-column",
      render: (val, record) => {
        return (
          <span>
            {record?.managingoffice ? record?.managingoffice : "-"}
          </span>
        );
      },
    });
  }

  const [form] = Form.useForm();

  const filters = useSelector(getFilters);
  const menuName = "qualitycommitment";
  const initialFilter = filters[menuName];

  const dispatch = useDispatch();

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    dispatch(setFilter(menuName, "name", values.name || []));
    dispatch(setFilter(menuName, "status", values.status || []));
    dispatch(setFilter(menuName, "managingoffice", values.managingoffice || null));
    if (values.year) {
      setSelectedYear(dayjs(values.year).format("YYYY"));
    }
  };

  const { getQualityCommitment } = queryKeys;

  const selectedManagingOffice = isSuperAdmin
    ? filters?.qualitycommitment?.managingoffice ||
    finduser?.managingoffice?.[0] ||
    managingOfficeList?.[0]
    : finduser?.managingoffice?.[0];

  useEffect(() => {
    if (
      isSuperAdmin &&
      !filters?.qualitycommitment?.managingoffice &&
      finduser?.managingoffice?.[0]
    ) {
      const defaultOffice = finduser.managingoffice[0];

      dispatch(setFilter(menuName, "managingoffice", defaultOffice));

      form.setFieldsValue({
        managingoffice: defaultOffice,
      });
    }
  }, [isSuperAdmin, finduser]);

  const { url } = getQualityCommitment(selectedYear, selectedManagingOffice);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchQualityCommitment = async () => {
      setIsLoading(true);
      const response = await CALL_FEATCH_API(url, "get");
      setData(response);
      setIsLoading(false);
    };
    fetchQualityCommitment();
  }, [url]);

  const dataList = useMemo(() => {
    if (!data || !activeUsers?.length) return [];
    const activeUserIds = new Set(activeUsers.map(u => u.userid));

    const responseData = data?.response?.response || [];

    return responseData
      .filter(item =>
        activeUserIds.has(item.userid) &&
        item?.managingoffice === selectedManagingOffice
      )
      .map(item => ({
        ...item,
        fullname: item.name,
        year: selectedYear,
        status: item.signedstatus ? "signed" : "not_signed",
        time: item.signeddate,
      }));
  }, [data, activeUsers, selectedManagingOffice, selectedYear]);

  const userOptions = (
    selectedManagingOffice === "Head Office"
      ? activeUsers
      : activeUsers?.filter(user => user?.managingoffice[0] === selectedManagingOffice)
  )?.map((user) => ({
    label: user?.fullname,
    value: user?.userid,
  }));

  const managingOfficeOption = managingOfficeList?.map((val) => {
    return {
      label: val,
      value: val,
    };
  });

  const statusOptions = [
    {
      label: "Signed",
      value: "signed",
    },
    {
      label: "Not Signed",
      value: "not_signed",
    }
  ];

  const uniqueByUserYear = (arr) => {
    const map = new Map();
    arr.forEach(item => {
      const key = `${item.userid}-${item.year}`;
      map.set(key, item);
    });
    return Array.from(map.values());
  };

  const getFilteredData = () => {
    const name = form.getFieldValue("name") || [];
    const status = form.getFieldValue("status") || [];
    const year = form.getFieldValue("year");
    let filteredUserList = new Set(
      userList
        ?.filter(user => user.statusid === "1")
        ?.map(user => user.userid)
    );

    let result = dataList?.filter(user => filteredUserList?.has(user.userid));

    if (name?.length > 0) {
      result = result?.filter(item =>
        name?.includes(item?.userid)
      );
    }

    if (status?.length > 0) {
      result = result?.filter(item =>
        status?.includes(item?.status)
      );
    }

    if (year && year?._d) {
      const selectedYear = moment(year?._d).format("YYYY");
      result = result?.filter(item =>
        String(item?.year) === selectedYear
      );
    }

    return uniqueByUserYear(result);
  };

  const xport = React.useCallback(() => {
    const filteredData = getFilteredData();
    const exportData = filteredData.map((item) => {
      const row = {
        Name: item.fullname || "-",
        Year: item.year || "-",
        Designation:
          item.designation === "null" || item.designation === ""
            ? "-"
            : item.designation || "-",
        Email:
          item.userid === "null" || item.userid === ""
            ? "-"
            : item.userid || "-",
        "Submission Status":
          item.status === "signed"
            ? "Signed"
            : item.status === "not_signed"
            ? "Not Signed"
            : "-",
        "Date Submitted":
          item.time &&
          item.time !== "null" &&
          item.time !== null &&
          item.time !== undefined
            ? moment(item.time).format("DD-MM-YYYY")
            : "-",
      };
      if (finduser?.managingoffice[0] === "Head Office") {
        row["Managing Office"] = item.managingoffice || "-";
      }
      return row;
    });

    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Quality Commitment");
    writeFileXLSX(wb, "Quality Commitment Report.csv");
  });

  return (
    <div>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Quality Commitment</Title>
            <ButtonOutlined className="text-end" disabled={getFilteredData()?.length === 0 || null || undefined} onClick={xport}>
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{ ...initialFilter }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="name"
                  label="Name"
                  options={userOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="status"
                  label="Status"
                  options={statusOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <Form.Item
                  name="year"
                  initialValue={dayjs()}
                >
                  <DatePicker
                    picker="year"
                    format="YYYY"
                    allowClear={false}
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "50px",
                      width: "100%",
                    }}
                    placeholder="Year"
                    disabledDate={(current) => current && current.year() > new Date().getFullYear() || current.year() < 2025}
                  />
                </Form.Item>
              </Col>
              {isSuperAdmin && (
                <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                  <Form.Item name="managingoffice">
                    <RoundedSelect
                      placeholder="Select managing office"
                      options={managingOfficeOption}
                      clearIcon={null}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={getFilteredData()}
        columns={columns}
        pagination={false}
        loading={isLoading}
        onRow={undefined}
        id="Table"
      />
    </div>
  );
}

export default QualityCommitment;